import React from 'react';
import PropTypes from 'prop-types';
// our components
import authContext from 'helpers/authContext';

/**
 * @description
 * Check user permissions.
 * @param {Object} ctx - Context.
 * @param {Array} allowPermissions - Permissions granted.
 * @param {boolean} matchAll - Exclusive permissions.
 * @param {Function} allowPermissionFunc - Predicate
 * to evaluate complex permissions.
 *
 * @returns {boolean} - The permission exist or not.
 */
export const checkPermissions = (
  ctx,
  allowPermissions,
  matchAll = false,
  allowPermissionFunc = null,
) => {
  const { allPermissions, rank } = ctx;
  if (rank === 'ADMIN') {
    return true;
  }

  if (allowPermissionFunc) {
    // Function to evaluate permissions.
    // This is a custom way to evaluate more complex expressions.
    return allowPermissionFunc(allPermissions);
  }

  const containArray = allowPermissions.some(Array.isArray);

  if (matchAll) {
    if (containArray) {
      return new Error(`matchAll can't be used with array values`);
    }
    // return true if all values from allowPermissions exist in allPermissions
    return allowPermissions.every(perm =>
      allPermissions.some(({ codename }) => perm === codename),
    );
  }

  if (containArray) {
    return allowPermissions.every(perm => {
      // if perm is an array,
      // return true if the user has at least one permission
      if (Array.isArray(perm)) {
        return perm.some(p =>
          allPermissions.some(({ codename }) => p === codename),
        );
      }
      // return true if the user has the permission
      return allPermissions.some(({ codename }) => perm === codename);
    });
  }

  // return true if the user has at least one permission
  return allowPermissions.some(perm =>
    allPermissions.some(({ codename }) => perm === codename),
  );
};

/**
 * @class
 * @extends React.Component
 */
class WithPermission extends React.Component {
  static contextType = authContext;

  /**
   * @description
   * Render component's children.
   */
  render() {
    const {
      children,
      alt,
      allowPermissions,
      matchAll,
      allowPermissionFunc,
    } = this.props;
    const allow = checkPermissions(
      this.context,
      allowPermissions,
      matchAll,
      allowPermissionFunc,
    );
    return allow ? children : alt;
  }
}

/**
 * WithPermission PropTypes
 * @param children - prop component's children
 */

WithPermission.propTypes = {
  children: PropTypes.node.isRequired,
  // eslint-disable-next-line react/require-default-props
  allowPermissions(props, propName) {
    // eslint-disable-next-line react/destructuring-assignment
    const prop = props[propName];
    if (!Array.isArray(prop)) return new Error(`${propName} must be an array`);
    let error = false;
    prop.forEach(elem => {
      if (typeof elem !== 'string' && !Array.isArray(elem)) {
        error = true;
      }
    });
    if (error) return Error(`${propName}  values must be string or array`);
    return null;
  },
  alt: PropTypes.node,
  matchAll: PropTypes.bool,
  allowPermissionFunc: PropTypes.func,
};
WithPermission.defaultProps = {
  allowPermissions: [],
  alt: null,
  matchAll: false,
  allowPermissionFunc: null,
};

export default WithPermission;
